import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Space, Tag } from 'antd';
import type { InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { ColumnsType, SortOrder } from 'antd/es/table/interface';

import { ICityTax } from '../../interfaces';
import { formatCpf, formatCurrency } from '../../utils/Masks';
import { formatDateStringToDate, formatDateStringToDateAndTime } from '../../utils/Conversores';
import { removeAcento } from '../../utils/RemoveAcentos';
import { useCityTaxesContext } from '../../contexts/CityTaxesContext';
import StatusTag from './components/StatusTag';
import { useGetColumnSearchPropsData } from '../../hooks/FilterTable/date';
import { ThemeContext } from '../../contexts/ThemeContext';
import TableActionButton from '../../components/TableActionButton';
import { IActiveFilterItem } from '../../components/ActiveFiltersBar';
import { findChunksText, HIGHLIGHT_STYLE } from '../../utils/Highlight';
import useTableColumnFilter from '../../hooks/useTableColumnFilter';
import { ECityTaxStatus } from '../../enums';

export const payment_modalities: Record<string, string> = {
    COTA_UNICA: 'Cota Única',
    PARCELA: 'Parcela',
};

export interface ITableData extends ICityTax {
    key: number;
    app_user_cpf: string;
}

export interface ICityTaxFilters {
    name: string | null;
    cpf: string | null;
    type: string | null;
    payment_modality: string | null;
    status: string | null;
    due_date: string | null;
    created_at: string | null;
}

export type SortField =
    | 'app_user_name'
    | 'value'
    | 'due_date'
    | 'type'
    | 'payment_modality'
    | 'status'
    | 'created_at'
    | null;

export default function useCityTaxes() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const { cityTaxes, loadCityTaxes } = useCityTaxesContext();
    const { getColumnRadioProps } = useTableColumnFilter();
    const nameInputRef = useRef<InputRef>(null);

    // Top-level custom search hooks
    const dueDateSearchProps = useGetColumnSearchPropsData({ dataIndex: 'due_date' });
    const createdDateSearchProps = useGetColumnSearchPropsData({ dataIndex: 'created_at' });

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rawData, setRawData] = useState<Array<ITableData>>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });

    const [filters, setFilters] = useState<ICityTaxFilters>({
        name: null,
        cpf: null,
        type: null,
        payment_modality: null,
        status: null,
        due_date: null,
        created_at: null,
    });

    const [sorter, setSorter] = useState<{ field: SortField; order: 'asc' | 'desc' | null }>({
        field: 'created_at',
        order: 'desc',
    });

    const [searchName, setSearchName] = useState(filters.name || '');
    const [searchCpf, setSearchCpf] = useState(filters.cpf || '');

    useEffect(() => {
        setSearchName(filters.name || '');
    }, [filters.name]);

    useEffect(() => {
        setSearchCpf(filters.cpf || '');
    }, [filters.cpf]);

    const handleFilterChange = useCallback((key: keyof ICityTaxFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({
            name: null,
            cpf: null,
            type: null,
            payment_modality: null,
            status: null,
            due_date: null,
            created_at: null,
        });
        setSearchName('');
        setSearchCpf('');
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const setTableDataFromRequest = useCallback((data: Array<ICityTax>) => {
        if (!Array.isArray(data)) return;
        const newData: ITableData[] = data.map((tax) => ({
            ...tax,
            key: tax.id || 0,
            app_user_cpf: tax.app_user?.cpf || '',
        }));
        setRawData(newData);
    }, []);

    useEffect(() => {
        setTableDataFromRequest(cityTaxes || []);
    }, [cityTaxes, setTableDataFromRequest]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadData() {
            try {
                setIsLoading(true);
                await loadCityTaxes(controller);
            } finally {
                setIsLoading(false);
            }
        }

        loadData();

        return () => {
            controller.abort();
        };
    }, [loadCityTaxes]);

    const filteredTableData = useMemo(() => {
        let result = rawData;

        if (filters.name) {
            const query = removeAcento(filters.name.trim().toLowerCase());
            result = result.filter((item) => {
                const name = item.app_user?.name || '';
                return removeAcento(name.toLowerCase()).includes(query);
            });
        }

        if (filters.cpf) {
            const cleanQuery = filters.cpf.replace(/\D/g, '');
            result = result.filter((item) => {
                const itemCpf = (item.app_user?.cpf || '').replace(/\D/g, '');
                return itemCpf.includes(cleanQuery);
            });
        }

        if (filters.type) {
            result = result.filter((item) => item.type === filters.type);
        }

        if (filters.payment_modality) {
            result = result.filter((item) => item.payment_modality === filters.payment_modality);
        }

        if (filters.status) {
            result = result.filter((item) => item.status === filters.status);
        }

        if (filters.due_date) {
            const query = filters.due_date.trim();
            result = result.filter((item) => {
                if (!item.due_date) return false;
                const formatted = formatDateStringToDate(item.due_date);
                return formatted.includes(query);
            });
        }

        if (filters.created_at) {
            const query = filters.created_at.trim();
            result = result.filter((item) => {
                if (!item.created_at) return false;
                const formatted = formatDateStringToDate(item.created_at);
                return formatted.includes(query);
            });
        }

        if (sorter.field && sorter.order) {
            result = [...result].sort((a, b) => {
                if (sorter.field === 'app_user_name') {
                    const comp = (a.app_user?.name || '').localeCompare(b.app_user?.name || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'value') {
                    const valA = Number(a.value) || 0;
                    const valB = Number(b.value) || 0;
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'due_date') {
                    const valA = new Date(a.due_date || 0).getTime();
                    const valB = new Date(b.due_date || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'type') {
                    const comp = (a.type || '').localeCompare(b.type || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'payment_modality') {
                    const comp = (a.payment_modality || '').localeCompare(b.payment_modality || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'status') {
                    const comp = (a.status || '').localeCompare(b.status || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'created_at') {
                    const valA = new Date(a.created_at || 0).getTime();
                    const valB = new Date(b.created_at || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                return 0;
            });
        }

        return result;
    }, [rawData, filters, sorter]);

    const stats = useMemo(() => {
        const total = rawData.length;
        const pending = rawData.filter((t) => t.status === ECityTaxStatus.processing).length;
        const approved = rawData.filter(
            (t) => t.status === ECityTaxStatus.approved || t.status === ECityTaxStatus.warning
        ).length;
        const refused = rawData.filter((t) => t.status === ECityTaxStatus.error).length;
        return { total, pending, approved, refused };
    }, [rawData]);

    const activeFilterItems: Array<IActiveFilterItem | null> = useMemo(() => [
        filters.name
            ? {
                key: 'name',
                label: 'Nome do Contribuinte',
                value: filters.name,
                onClear: () => {
                    handleFilterChange('name', null);
                    setSearchName('');
                },
            }
            : null,
        filters.cpf
            ? {
                key: 'cpf',
                label: 'CPF',
                value: formatCpf(filters.cpf),
                onClear: () => {
                    handleFilterChange('cpf', null);
                    setSearchCpf('');
                },
            }
            : null,
        filters.type
            ? {
                key: 'type',
                label: 'Tipo de Imposto',
                value: filters.type,
                onClear: () => handleFilterChange('type', null),
            }
            : null,
        filters.payment_modality
            ? {
                key: 'payment_modality',
                label: 'Modalidade',
                value: payment_modalities[filters.payment_modality] || filters.payment_modality,
                onClear: () => handleFilterChange('payment_modality', null),
            }
            : null,
        filters.status
            ? {
                key: 'status',
                label: 'Status',
                value: filters.status,
                onClear: () => handleFilterChange('status', null),
            }
            : null,
        filters.due_date
            ? {
                key: 'due_date',
                label: 'Data de Vencimento',
                value: formatDateStringToDate(filters.due_date),
                onClear: () => handleFilterChange('due_date', null),
            }
            : null,
        filters.created_at
            ? {
                key: 'created_at',
                label: 'Data de Cadastro',
                value: formatDateStringToDate(filters.created_at),
                onClear: () => handleFilterChange('created_at', null),
            }
            : null,
    ], [filters, handleFilterChange]);

    const getSortOrder = useCallback((field: SortField): SortOrder | undefined => {
        if (sorter.field === field) {
            return sorter.order === 'asc' ? 'ascend' : sorter.order === 'desc' ? 'descend' : undefined;
        }
        return undefined;
    }, [sorter]);

    const handleTableChange = (_: any, __: any, newSorter: any) => {
        if (newSorter && newSorter.field) {
            setSorter({
                field: newSorter.field as SortField,
                order: newSorter.order === 'ascend' ? 'asc' : newSorter.order === 'descend' ? 'desc' : null,
            });
        }
    };

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const columns: ColumnsType<ITableData> = useMemo(() => [
        {
            title: 'Contribuinte / Cidadão',
            dataIndex: 'app_user_name',
            key: 'app_user_name',
            sorter: true,
            sortOrder: getSortOrder('app_user_name'),
            showSorterTooltip: false,
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                            Nome
                        </div>
                        <Input
                            ref={nameInputRef}
                            placeholder="Buscar por nome..."
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            onPressEnter={() => {
                                confirm();
                                setFilters((prev) => ({
                                    ...prev,
                                    name: searchName || null,
                                    cpf: searchCpf || null,
                                }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            allowClear
                        />
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                            CPF
                        </div>
                        <Input
                            placeholder="000.000.000-00"
                            value={searchCpf}
                            onChange={(e) => setSearchCpf(formatCpf(e.target.value))}
                            onPressEnter={() => {
                                confirm();
                                setFilters((prev) => ({
                                    ...prev,
                                    name: searchName || null,
                                    cpf: searchCpf || null,
                                }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            allowClear
                        />
                    </div>
                    <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                confirm();
                                setFilters((prev) => ({
                                    ...prev,
                                    name: searchName || null,
                                    cpf: searchCpf || null,
                                }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchName('');
                                setSearchCpf('');
                                if (clearFilters) clearFilters();
                                confirm();
                                setFilters((prev) => ({
                                    ...prev,
                                    name: null,
                                    cpf: null,
                                }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: (filters.name || filters.cpf) ? '#1677ff' : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => nameInputRef.current?.focus(), 100);
                }
            },
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span
                        style={{
                            fontWeight: 600,
                            color: isDark ? '#f0f0f0' : '#1e293b',
                            fontSize: 13,
                            lineHeight: '1.3',
                        }}
                    >
                        {filters.name ? (
                            <Highlighter
                                highlightStyle={HIGHLIGHT_STYLE}
                                searchWords={[filters.name]}
                                findChunks={findChunksText}
                                textToHighlight={record.app_user?.name || '--'}
                            />
                        ) : (
                            record.app_user?.name || '--'
                        )}
                    </span>
                    <span
                        style={{
                            color: isDark ? '#8c8c8c' : '#64748b',
                            fontSize: 12,
                            fontWeight: 500,
                            letterSpacing: '0.3px',
                            whiteSpace: 'nowrap',
                            lineHeight: '1.2',
                        }}
                    >
                        {filters.cpf ? (
                            <Highlighter
                                highlightStyle={HIGHLIGHT_STYLE}
                                searchWords={[filters.cpf]}
                                findChunks={findChunksText}
                                textToHighlight={record.app_user?.cpf ? formatCpf(record.app_user.cpf) : '--'}
                            />
                        ) : (
                            record.app_user?.cpf ? formatCpf(record.app_user.cpf) : '--'
                        )}
                    </span>
                </div>
            ),
        },
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            sorter: true,
            sortOrder: getSortOrder('type'),
            showSorterTooltip: false,
            ...getColumnRadioProps({
                title: 'Filtrar por Tributo',
                value: filters.type || 'ALL',
                onChange: (val) => handleFilterChange('type', val === 'ALL' ? null : val),
                isActive: Boolean(filters.type),
                options: [
                    { label: 'Todos', value: 'ALL' },
                    { label: 'IPTU', value: 'IPTU' },
                    { label: 'ITBI', value: 'ITBI' },
                ],
            }),
            render: (type: string) => (
                <Tag
                    color={type === 'IPTU' ? 'blue' : 'cyan'}
                    style={{ fontWeight: 600, borderRadius: 6 }}
                >
                    {type || '--'}
                </Tag>
            ),
        },
        {
            title: 'Modalidade',
            dataIndex: 'payment_modality',
            key: 'payment_modality',
            sorter: true,
            sortOrder: getSortOrder('payment_modality'),
            showSorterTooltip: false,
            ...getColumnRadioProps({
                title: 'Filtrar por Modalidade',
                value: filters.payment_modality || 'ALL',
                onChange: (val) => handleFilterChange('payment_modality', val === 'ALL' ? null : val),
                isActive: Boolean(filters.payment_modality),
                options: [
                    { label: 'Todas', value: 'ALL' },
                    { label: 'Cota Única', value: 'COTA_UNICA' },
                    { label: 'Parcela', value: 'PARCELA' },
                ],
            }),
            render: (modality: string, record: ITableData) => {
                const label = payment_modalities[modality] || modality || '--';
                return (
                    <span style={{ color: isDark ? '#cbd5e1' : '#475569', whiteSpace: 'nowrap' }}>
                        {label}
                        {record.installment ? ` (${record.installment}ª)` : ''}
                    </span>
                );
            },
        },
        {
            title: 'Valor',
            dataIndex: 'value',
            key: 'value',
            align: 'right' as const,
            sorter: true,
            sortOrder: getSortOrder('value'),
            showSorterTooltip: false,
            width: 140,
            render: (value: number) => (
                <span
                    style={{
                        fontWeight: 600,
                        color: isDark ? '#f1f5f9' : '#0f172a',
                        whiteSpace: 'nowrap',
                        display: 'inline-block',
                    }}
                >
                    {value ? `R$ ${formatCurrency(value)}` : 'R$ 0,00'}
                </span>
            ),
        },
        {
            title: 'Vencimento',
            dataIndex: 'due_date',
            key: 'due_date',
            sorter: true,
            sortOrder: getSortOrder('due_date'),
            showSorterTooltip: false,
            ...dueDateSearchProps,
            render: (date: any) => (
                <span style={{ fontSize: 12.5, whiteSpace: 'nowrap', color: isDark ? '#cbd5e1' : '#475569' }}>
                    {date ? formatDateStringToDate(date) : '--'}
                </span>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: true,
            sortOrder: getSortOrder('status'),
            showSorterTooltip: false,
            ...getColumnRadioProps({
                title: 'Filtrar por Status',
                value: filters.status || 'ALL',
                onChange: (val) => handleFilterChange('status', val === 'ALL' ? null : val),
                isActive: Boolean(filters.status),
                options: [
                    { label: 'Todos', value: 'ALL' },
                    { label: 'Em análise', value: ECityTaxStatus.processing },
                    { label: 'Aprovado', value: ECityTaxStatus.approved },
                    { label: 'Alerta', value: ECityTaxStatus.warning },
                    { label: 'Recusado', value: ECityTaxStatus.error },
                ],
            }),
            render: (_: any, record: ITableData) => (
                <StatusTag status={record.status} message={record.message} />
            ),
        },
        {
            title: 'Data de Cadastro',
            dataIndex: 'created_at',
            key: 'created_at',
            sorter: true,
            sortOrder: getSortOrder('created_at'),
            showSorterTooltip: false,
            ...createdDateSearchProps,
            render: (date: any) => (
                <span style={{ fontSize: 12.5, whiteSpace: 'nowrap', color: isDark ? '#cbd5e1' : '#475569' }}>
                    {date ? formatDateStringToDateAndTime(date) : '--'}
                </span>
            ),
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 80,
            render: (_: any, record: ITableData) => (
                <Space size={4}>
                    <TableActionButton
                        action="view"
                        tooltip="Visualizar detalhes do imposto"
                        onClick={() => navigate(`/impostos/municipais/${record.id}`)}
                    />
                </Space>
            ),
        },
    ], [
        isDark,
        filters,
        getSortOrder,
        searchName,
        searchCpf,
        getColumnRadioProps,
        dueDateSearchProps,
        createdDateSearchProps,
        handleFilterChange,
        navigate,
    ]);

    return {
        isLoading,
        columns,
        tableData: filteredTableData,
        stats,
        pagination,
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        navigate,
    };
}
